// components/session-data/SessionDataProvider.jsx
import React, { createContext, useContext, useLayoutEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { db } from "../../firebase/firebase.utils";
import { setTasks } from "../../redux/tasks/tasks.actions";
import { setNotes } from "../../redux/notes/notes.actions";
import { setPrjTasks } from "../../redux/prjtasks/prjtasks.actions";
import { setPrjNotes } from "../../redux/prjnotes/prjnotes.actions";
import { setPrjPhases } from "../../redux/prjphases/prjphases.actions";
import { setModules, setActiveModules } from "../../redux/space/space.actions";
import { setFiles } from "../../redux/files/files.reducer";
import { createProjectCache } from "../../utils/projectCache";

const Context = createContext(null);
export function useSessionData() {
  const value = useContext(Context);
  if (!value) throw new Error("Mount SessionDataProvider inside ChatLayout.");
  return value;
}

export default function SessionDataProvider({ children }) {
  const uid = useSelector((state) => state.user.currentUser?.uid);
  return <SessionData key={uid || "signed-out"} uid={uid}>{children}</SessionData>;
}

function SessionData({ uid, children }) {
  const dispatch = useDispatch();
  const [ready, setReady] = useState(false);
  const [inboxTasks, setInboxTasks] = useState([]);
  const [errors, setErrors] = useState({});
  const [projectCache] = useState(() => createProjectCache(db));

  useLayoutEffect(() => {
    let active = true;
    const clearRedux = () => {
      dispatch(setTasks([]));
      dispatch(setNotes([]));
      dispatch(setPrjTasks([]));
      dispatch(setPrjNotes([]));
      dispatch(setPrjPhases([]));
      dispatch(setModules([]));
      dispatch(setActiveModules([]));
      dispatch(setFiles({ key: null, reset: true, projectLoading: false }));
    };
    clearRedux();
    if (!uid) return () => projectCache.dispose();
    const listen = (query, scope, apply) => query.onSnapshot(
      (snap) => {
        if (!active) return;
        apply(snap.docs.map((doc) => ({
          ...doc.data({ serverTimestamps: "estimate" }), id: doc.id,
        })));
      },
      (error) => {
        if (!active) return;
        apply([]);
        setErrors((previous) => ({ ...previous, [scope]: `Could not load ${scope}.` }));
        console.error(`${scope} subscription failed:`, error);
      },
    );
    const userRef = db.collection("users").doc(uid);
    const stops = [
      listen(userRef.collection("tasks"), "quick tasks", (tasks) => setInboxTasks(
        tasks.map((task) => ({ ...task, start: task.startdate, end: task.enddate, durationn: task.duration })),
      )),
      listen(userRef.collection("notes"), "quick notes", (notes) => dispatch(setNotes(notes))),
      listen(db.collectionGroup("tasks").where("assign", "==", uid), "assigned tasks", (tasks) => dispatch(setTasks(tasks))),
    ];
    setReady(true);
    return () => {
      active = false;
      stops.forEach((stop) => stop());
      projectCache.dispose();
      clearRedux();
    };
  }, [uid, dispatch, projectCache]);

  const value = useMemo(() => ({ inboxTasks, errors, projectCache }), [inboxTasks, errors, projectCache]);
  return <Context.Provider value={value}>{ready ? children : null}</Context.Provider>;
}
